import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DropdownMenu } from './DropdownMenu';
import { useStore } from '../store/useStore';
const winAction = (action: 'minimize' | 'toggleMaximize' | 'close') => {
  import('@tauri-apps/api/window').then(({ getCurrentWindow }) => {
    const w = getCurrentWindow();
    if (action === 'minimize') w.minimize();
    if (action === 'toggleMaximize') w.toggleMaximize();
    if (action === 'close') w.close();
  }).catch((e) => {
    console.error('Window action failed:', e);
  });
};

const WindowControls = () => (
  <div className="flex items-stretch h-full ml-1" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
    <div className="w-px h-4 bg-white/20 self-center mx-1" />
    {/* Minimize */}
    <button
      onClick={() => winAction('minimize')}
      className="w-[46px] flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
      title="Minimize"
    >
      <svg width="12" height="1" viewBox="0 0 12 1"><rect width="12" height="1" fill="white" opacity="0.9"/></svg>
    </button>
    {/* Maximize */}
    <button
      onClick={() => winAction('toggleMaximize')}
      className="w-[46px] flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
      title="Maximize"
    >
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><rect x="1" y="1" width="9" height="9" rx="1" stroke="white" strokeWidth="1.2" opacity="0.9"/></svg>
    </button>
    {/* Close */}
    <button
      onClick={() => {
        if (typeof (window as any).showExitConfirm === 'function') {
          (window as any).showExitConfirm();
        } else {
          winAction('close');
        }
      }}
      className="w-[46px] flex items-center justify-center hover:bg-[#e81123] active:bg-[#c41019] transition-colors"
      title="Close"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <line x1="1.5" y1="1.5" x2="10.5" y2="10.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" opacity="0.9"/>
        <line x1="10.5" y1="1.5" x2="1.5" y2="10.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" opacity="0.9"/>
      </svg>
    </button>
  </div>
);

const GlobalSearchBar = ({ isLocked }: { isLocked: boolean }) => {
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const all: { label: string; category: string; action: () => void }[] = [];

    // Menu items
    const menuItems = [
      { label: 'Master Creation', category: 'Masters', level: 'creation' },
      { label: 'Master Alteration', category: 'Masters', level: 'alteration' },
      { label: 'Accounts List', category: 'Masters', level: 'accountsList' },
      { label: 'Vouchers', category: 'Transactions', level: 'vouchers' },
      { label: 'Quotations', category: 'Transactions', level: 'quotation_list' },
      { label: 'Day Book', category: 'Transactions', level: 'daybook' },
      { label: 'Trial Balance', category: 'Reports', level: 'display_trialbalance' },
      { label: 'Balance Sheet', category: 'Reports', level: 'balancesheet' },
      { label: 'Profit & Loss', category: 'Reports', level: 'profitloss' },
      { label: 'Cash Flow', category: 'Reports', level: 'display_cashflow' },
      { label: 'Funds Flow', category: 'Reports', level: 'display_fundsflow' },
      { label: 'Ratio Analysis', category: 'Reports', level: 'ratioanalysis' },
      { label: 'Stock Summary', category: 'Reports', level: 'display_stocksummary' },
      { label: 'Bank Reconciliation', category: 'Reports', level: 'bank_reconciliation' },
      { label: 'GSTR-1', category: 'GST', level: 'gstr1' },
      { label: 'GSTR-3B', category: 'GST', level: 'gstr3b' },
      { label: 'GST Rate Summary', category: 'GST', level: 'gst_summary' },
      { label: 'Employees', category: 'HR', level: 'employees' },
      { label: 'Payroll', category: 'HR', level: 'payroll' },
      { label: 'Salary Processing', category: 'HR', level: 'payroll_processing' },
      { label: 'Ledger', category: 'Create', level: 'creation_ledger' },
      { label: 'Group', category: 'Create', level: 'creation_group' },
      { label: 'Stock Item', category: 'Create', level: 'creation_stockitem' },
      { label: 'Customization', category: 'Settings', level: 'customization' },
    ];

    menuItems.forEach(m => {
      if (m.label.toLowerCase().includes(q) || m.category.toLowerCase().includes(q)) {
        all.push({
          label: m.label,
          category: m.category,
          action: () => {
            if (typeof (window as any).__navigateToLevel === 'function') {
              (window as any).__navigateToLevel(m.level);
            }
          },
        });
      }
    });

    return all.slice(0, 10);
  }, [query]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  // Scroll selected into view
  useEffect(() => {
    if (dropdownRef.current) {
      const el = dropdownRef.current.children[selectedIdx] as HTMLElement;
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      results[selectedIdx]?.action();
      setQuery('');
      setIsFocused(false);
      inputRef.current?.blur();
      if (typeof (window as any).__setMasterSearch === 'function') (window as any).__setMasterSearch('');
    } else if (e.key === 'Escape') {
      setQuery('');
      setIsFocused(false);
      inputRef.current?.blur();
      if (typeof (window as any).__setMasterSearch === 'function') (window as any).__setMasterSearch('');
    }
  };

  return (
    <div className="relative mx-4 flex-1 max-w-[400px]" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
      <div className={`flex items-center rounded-lg px-3 py-[5px] transition-all ${
        isLocked ? 'opacity-40 cursor-not-allowed bg-white/5'
        : isFocused ? 'bg-white/20 ring-1 ring-white/30'
        : 'bg-white/10 hover:bg-white/15'
      }`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="opacity-50 mr-2 flex-shrink-0">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
        <input
          ref={inputRef}
          id="global-search-input"
          type="text"
          value={query}
          placeholder="Search menu, report, action..."
          disabled={isLocked}
          className="bg-transparent border-none outline-none text-white text-[12px] font-medium flex-1 placeholder:text-white/40 w-full"
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
        {!query && <span className="text-[9px] text-white/25 font-mono bg-white/8 px-1.5 py-[1px] rounded ml-2 flex-shrink-0 select-none">Ctrl /</span>}
        {query && <span className="text-[9px] text-white/40 ml-1 select-none">{results.length} found</span>}
      </div>

      {/* Live Dropdown */}
      {isFocused && query.trim() && results.length > 0 && (
        <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-[200] max-h-[320px] overflow-y-auto">
          {results.map((r, idx) => (
            <div key={idx}
              onMouseDown={(e) => { e.preventDefault(); r.action(); setQuery(''); setIsFocused(false); }}
              onMouseEnter={() => setSelectedIdx(idx)}
              className={`flex items-center justify-between px-3 py-2 cursor-pointer text-[12px] ${
                selectedIdx === idx ? 'bg-[#1e8585] text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}>
              <span className="font-semibold">{r.label}</span>
              <span className={`text-[10px] font-medium ${selectedIdx === idx ? 'text-white/60' : 'text-gray-400'}`}>{r.category}</span>
            </div>
          ))}
        </div>
      )}
      {isFocused && query.trim() && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-[200]">
          <div className="px-3 py-3 text-center text-[12px] text-gray-400">No results for "{query}"</div>
        </div>
      )}
    </div>
  );
};

export const TopHeader = ({ openMenu, setOpenMenu, onDropdownAction, config, dropdownIndex }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const licenseInfo   = useStore((state) => state.licenseInfo);
  const isLocked = !activeCompany;
  const isTrialMode = licenseInfo?.type === 'demo' || !licenseInfo;

  const TopMenuLink = ({ shortcut, label, menuKey }: any) => {
    // NOTE: 'D: Data' menu internally uses menuKey 'Y' (not 'D') to avoid conflict
    // with 'D: Delete' inside the Company (C) menu. Display shortcut is 'D', but
    // isAlwaysAvailable checks 'Y'. Do not change without updating both places.
    const isAlwaysAvailable = menuKey === 'C' || menuKey === 'Y';
    const active = isAlwaysAvailable || !isLocked;
    const isOpen = openMenu === menuKey && active;

    return (
      <div className="relative" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
        <div
          className={`flex items-center px-2 py-1 space-x-1 select-none ${
            isOpen
              ? 'bg-[#15666b]'
              : !active
              ? 'opacity-40 cursor-not-allowed pointer-events-none'
              : 'hover:bg-[#15666b] cursor-pointer'
          }`}
          onClick={(e) => { e.stopPropagation(); if (active) setOpenMenu(isOpen ? null : menuKey); }}
        >
          <span className="underline font-bold text-[#cffafe]">{shortcut}</span>
          <span>: {label}</span>
        </div>
        {isOpen && <DropdownMenu menuKey={menuKey} config={config} onAction={onDropdownAction} isLocked={isLocked} isTrialMode={isTrialMode} dropdownIndex={isOpen ? dropdownIndex : -1} />}
      </div>
    );
  };

  return (
    <div
      data-tauri-drag-region
      className="bg-[#1e8585] text-white flex flex-row items-center justify-between px-2 text-xs shadow-md relative z-[100] shrink-0"
      style={{
        // Extend into the title bar area when window-controls-overlay is active
        // This makes the teal bar fill the purple OS title bar area
        height: 'calc(env(titlebar-area-height, 32px) + 28px)',
        paddingTop: 'env(titlebar-area-height, 0px)',
        WebkitAppRegion: 'drag',
        // Prevent text selection while dragging
        userSelect: 'none',
      } as React.CSSProperties}
    >
      {/* Left — brand + icon menus */}
      <div className="flex items-center h-full" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
        <div className="flex items-center gap-2 px-3 select-none" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
          <img src="/favicon.png" alt="" className="w-5 h-5 rounded-[3px]" draggable={false} />
          <span className="text-white font-bold text-sm tracking-wide">Aarthika Pro</span>
        </div>
        <div className="w-px h-4 bg-[#52b3b8] mx-1" />
        <div className="flex items-center h-full ml-1">
          {/* Company & Data — Settings gear icon (merged) */}
          <div className="relative" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
            <div
              className={`flex items-center px-2.5 py-1.5 select-none rounded-lg mx-0.5 transition-colors ${
                openMenu === 'C' || openMenu === 'Y' ? 'bg-white/15' : 'hover:bg-white/10 cursor-pointer'
              }`}
              onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === 'C' ? null : 'C'); }}
              title="Company & Settings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="opacity-90">
                <circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            </div>
            {openMenu === 'C' && <DropdownMenu menuKey="C" config={config} onAction={onDropdownAction} isLocked={isLocked} isTrialMode={isTrialMode} dropdownIndex={openMenu === 'C' ? dropdownIndex : -1} />}
          </div>
        </div>
      </div>

      {/* Center — Global Search with Live Dropdown */}
      <GlobalSearchBar isLocked={isLocked} />

      {/* Right — Icon menus + Window Controls */}
      <div className="flex items-center h-full" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
        <div className="flex items-center gap-0.5">
          {/* GST */}
          <div className="relative" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
            <div className={`flex items-center gap-1 px-2 py-1 select-none rounded-md mx-0.5 transition-colors text-[11px] font-bold ${
              !isLocked ? (openMenu === 'G' ? 'bg-white/15' : 'hover:bg-white/10 cursor-pointer') : 'opacity-40 cursor-not-allowed pointer-events-none'
            }`} onClick={(e) => { e.stopPropagation(); if (!isLocked) setOpenMenu(openMenu === 'G' ? null : 'G'); }} title="GST Returns">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 15h6"/><path d="M9 11h6"/></svg>
              <span>GST</span>
            </div>
            {openMenu === 'G' && !isLocked && <DropdownMenu menuKey="G" config={config} onAction={onDropdownAction} isLocked={isLocked} isTrialMode={isTrialMode} dropdownIndex={dropdownIndex} />}
          </div>
          {/* Import */}
          <div className="relative" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
            <div className={`flex items-center px-2 py-1 select-none rounded-md mx-0.5 transition-colors ${
              !isLocked ? (openMenu === 'O' ? 'bg-white/15' : 'hover:bg-white/10 cursor-pointer') : 'opacity-40 cursor-not-allowed pointer-events-none'
            }`} onClick={(e) => { e.stopPropagation(); if (!isLocked) setOpenMenu(openMenu === 'O' ? null : 'O'); }} title="Import Data">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </div>
            {openMenu === 'O' && !isLocked && <DropdownMenu menuKey="O" config={config} onAction={onDropdownAction} isLocked={isLocked} isTrialMode={isTrialMode} dropdownIndex={dropdownIndex} />}
          </div>
          {/* Export */}
          <div className="relative" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
            <div className={`flex items-center px-2 py-1 select-none rounded-md mx-0.5 transition-colors ${
              !isLocked ? (openMenu === 'E' ? 'bg-white/15' : 'hover:bg-white/10 cursor-pointer') : 'opacity-40 cursor-not-allowed pointer-events-none'
            }`} onClick={(e) => { e.stopPropagation(); if (!isLocked) setOpenMenu(openMenu === 'E' ? null : 'E'); }} title="Export Data">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </div>
            {openMenu === 'E' && !isLocked && <DropdownMenu menuKey="E" config={config} onAction={onDropdownAction} isLocked={isLocked} isTrialMode={isTrialMode} dropdownIndex={dropdownIndex} />}
          </div>
          {/* Print */}
          <div className="relative" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
            <div className={`flex items-center px-2 py-1 select-none rounded-md mx-0.5 transition-colors ${
              !isLocked ? (openMenu === 'P' ? 'bg-white/15' : 'hover:bg-white/10 cursor-pointer') : 'opacity-40 cursor-not-allowed pointer-events-none'
            }`} onClick={(e) => { e.stopPropagation(); if (!isLocked) setOpenMenu(openMenu === 'P' ? null : 'P'); }} title="Print">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-90"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            </div>
            {openMenu === 'P' && !isLocked && <DropdownMenu menuKey="P" config={config} onAction={onDropdownAction} isLocked={isLocked} isTrialMode={isTrialMode} dropdownIndex={dropdownIndex} />}
          </div>
        </div>
        <WindowControls />
      </div>
    </div>
  );
};